/**
 * Root-Cause Analysis - Step 4: Root Cause Analysis Results
 *
 * Performs statistical analysis comparing filtered subset (anomalous) vs base dataset.
 * Displays Chi-squared test results and Odds Ratios for each attribute value.
 */

import React, { useState, useEffect } from 'react';
import { makePostRequest } from '../../../services/apiClient';
import ApplyFilterModal from './components/ApplyFilterModal';

const SortableHeader = ({ column, children, onSort, activeSortBy, sortAsc, onReset }) => (
  <th
    onClick={() => onSort(column)}
    className="px-3 py-2 text-left border border-gray-300 text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200"
  >
    {activeSortBy === column && onReset && (
      <button
        onClick={(e) => { e.stopPropagation(); onReset(); }}
        className="block mb-0.5 text-xs text-gray-400 hover:text-gray-700 font-normal leading-none"
        title="Reset to default sort"
      >
        ↺ reset
      </button>
    )}
    <div className="flex items-center gap-1">
      {children}
      {activeSortBy === column && (
        <span className="text-blue-600">{sortAsc ? '↑' : '↓'}</span>
      )}
    </div>
  </th>
);

const Step4_RootCauseResults = ({
  activeFile,
  selectedObjectTypes,
  selectedAttributes,
  dateRange,
  edgeDurationFilters,
  edgeContainmentFilters,
  activityNodeFilters,
  eventAttributeFilters,
  objectAttributeFilters,
  initialResults,
  onBack
}) => {
  const [results, setResults] = useState(initialResults || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasAnalyzed, setHasAnalyzed] = useState(!!initialResults);
  const [significanceThreshold] = useState(0.05); // Removed setter - now constant (mandatory quality gate)
  const [sortBy, setSortBy] = useState('chi_squared'); // Changed default to chi_squared
  const [sortAsc, setSortAsc] = useState(false); // Still descending
  // Removed showOnlySignificant state - always ON (mandatory quality gate)
  const [selectedKeys, setSelectedKeys] = useState(new Set()); // Row selection for "apply as filter"
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // Clear selection whenever the underlying results change (re-analysis)
  useEffect(() => {
    setSelectedKeys(new Set());
  }, [results]);

  // Auto-trigger analysis on mount (only if no initial results provided)
  useEffect(() => {
    if (!initialResults && !hasAnalyzed && !loading && !results) {
      console.log('Step 4 mounted - auto-triggering root cause analysis');
      handleAnalyze();
    }
  }, []); // Empty dependency array = run once on mount

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    setHasAnalyzed(true);

    try {
      console.log('Step 3 - dateRange prop received:', dateRange);

      const payload = {
        file_type: activeFile,
        selected_object_types: selectedObjectTypes,
        selected_attributes: selectedAttributes,
        date_range: dateRange && dateRange.enabled && (dateRange.startDate || dateRange.endDate) ? {
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          enabled: true
        } : undefined,
        edge_duration_filters: edgeDurationFilters && edgeDurationFilters.length > 0 ? edgeDurationFilters : undefined,
        edge_containment_filters: edgeContainmentFilters && edgeContainmentFilters.enabled && edgeContainmentFilters.edges.length > 0
          ? edgeContainmentFilters
          : undefined,
        activity_node_filters: activityNodeFilters && activityNodeFilters.enabled && activityNodeFilters.nodes.length > 0
          ? activityNodeFilters
          : undefined,
        event_attribute_filter: (eventAttributeFilters && eventAttributeFilters.enabled && eventAttributeFilters.conditions && eventAttributeFilters.conditions.some(c => c.attribute && c.value))
          ? eventAttributeFilters
          : undefined,
        object_attribute_filter: (objectAttributeFilters?.enabled && objectAttributeFilters?.conditions?.some(c => c.attribute && c.value))
          ? objectAttributeFilters
          : undefined,
        significance_threshold: significanceThreshold
      };

      console.log('Performing root cause analysis with payload:', payload);

      // Warn if no filters applied
      const hasFilters = payload.date_range || payload.edge_duration_filters || payload.edge_containment_filters || payload.activity_node_filters || payload.event_attribute_filter || payload.object_attribute_filter;
      if (!hasFilters) {
        console.warn('WARNING: No filters applied - filtered and unfiltered datasets will be identical!');
      }

      const response = await makePostRequest('/rootcause/analyze-step3', payload);
      console.log('Root cause analysis response:', response);
      setResults(response);
    } catch (err) {
      console.error('Error performing root cause analysis:', err);
      setError(err.message || 'Failed to perform root cause analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortAsc(!sortAsc);
    } else {
      setSortBy(column);
      setSortAsc(true);
    }
  };

  const handleReset = (sortBy !== 'chi_squared' || sortAsc !== false)
    ? () => { setSortBy('chi_squared'); setSortAsc(false); }
    : undefined;

  const getSortedResults = () => {
    if (!results || !results.results) return {};

    // ALWAYS filter by significance (mandatory quality gate)
    const significantResults = results.results.filter(
      r => r.p_value < significanceThreshold && r.direction !== 'neutral'
    );

    // Group by object_type first, then by attribute
    const grouped = significantResults.reduce((acc, result) => {
      if (!acc[result.object_type]) {
        acc[result.object_type] = {};
      }
      if (!acc[result.object_type][result.attribute]) {
        acc[result.object_type][result.attribute] = [];
      }
      acc[result.object_type][result.attribute].push(result);
      return acc;
    }, {});

    // Sort within each group
    Object.keys(grouped).forEach(objType => {
      Object.keys(grouped[objType]).forEach(attr => {
        grouped[objType][attr].sort((a, b) => {
          let comparison = 0;

          switch (sortBy) {
            case 'chi_squared':
              comparison = b.chi_squared - a.chi_squared;  // Descending by default
              break;
            case 'lift':
              comparison = b.lift - a.lift;  // Descending by default
              break;
            case 'p_value':
              comparison = a.p_value - b.p_value;  // Ascending
              break;
            case 'odds_ratio':
              comparison = b.odds_ratio - a.odds_ratio;  // Descending
              break;
            case 'value':
              comparison = a.value.localeCompare(b.value);
              break;
            case 'direction':
              comparison = a.direction.localeCompare(b.direction);
              break;
            case 'object_type':
              comparison = a.object_type.localeCompare(b.object_type);
              break;
            case 'percentage_filtered':
              comparison = b.percentage_filtered - a.percentage_filtered;
              break;
            case 'percentage_unfiltered':
              comparison = b.percentage_unfiltered - a.percentage_unfiltered;
              break;
            default:
              comparison = 0;
          }

          return sortAsc ? -comparison : comparison;
        });
      });
    });

    // Sort object type keys alphabetically
    const sortedObjTypes = Object.keys(grouped).sort();
    const sortedGrouped = {};
    sortedObjTypes.forEach(objType => {
      sortedGrouped[objType] = {};
      // Sort attribute keys alphabetically within each object type
      const sortedAttrs = Object.keys(grouped[objType]).sort();
      sortedAttrs.forEach(attr => {
        sortedGrouped[objType][attr] = grouped[objType][attr];
      });
    });

    return sortedGrouped;
  };

  const getPValueArrow = (pValue, threshold) => {
    if (pValue < threshold) return '↓';
    return '↑';
  };

  const getDirectionBadge = (direction, oddsRatio) => {
    if (direction === 'over_represented') {
      return (
        <span
          className="inline-block px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700 cursor-help"
          title={`Odds Ratio: ${oddsRatio.toFixed(2)}`}
        >
          Over-represented
        </span>
      );
    } else if (direction === 'under_represented') {
      return (
        <span
          className="inline-block px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700 cursor-help"
          title={`Odds Ratio: ${oddsRatio.toFixed(2)}`}
        >
          Under-represented
        </span>
      );
    }
  };


  const groupedResults = getSortedResults();

  // Calculate total count for summary (nested structure: objectType -> attribute -> values)
  const totalResultsCount = Object.values(groupedResults).reduce((sum, attributes) =>
    sum + Object.values(attributes).reduce((attrSum, values) => attrSum + values.length, 0)
  , 0);

  // Map of selectable row key -> result (keys are never split back into parts,
  // values may contain any character)
  const rowKey = (result) => `${result.object_type}|${result.attribute}|${result.value}`;
  const visibleRowMap = new Map();
  Object.values(groupedResults).forEach((attributes) =>
    Object.values(attributes).forEach((values) =>
      values.forEach((result) => visibleRowMap.set(rowKey(result), result))
    )
  );
  const visibleKeys = [...visibleRowMap.keys()];
  const allSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selectedKeys.has(k));
  const someSelected = visibleKeys.some((k) => selectedKeys.has(k));

  const toggleRowSelection = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedKeys(allSelected ? new Set() : new Set(visibleKeys));
  };

  const selectedConditions = [...selectedKeys]
    .map((key) => visibleRowMap.get(key))
    .filter(Boolean)
    .map((result) => ({
      objectType: result.object_type,
      attribute: result.attribute,
      value: result.value,
    }));

  return (
    <div className="flex flex-col p-6">
      {/* Header */}
      <div className="mb-4 flex-shrink-0">
        <h2 className="text-xl font-semibold text-gray-800">Step 4: Root Cause Analysis</h2>
        <p className="text-sm text-gray-600 mt-1">
          Statistical analysis comparing filtered subset vs. base dataset
        </p>
      </div>

      {/* Scrollable content area */}
      <div className="overflow-y-auto" style={{ maxHeight: '500px' }}>
        {/* Summary */}
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="mb-2">
          <p className="text-xs font-medium text-gray-700 mb-1">
            Selected Object Types ({selectedObjectTypes.length}):
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedObjectTypes.map((objType) => (
              <span
                key={objType}
                className="inline-block px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded"
              >
                {objType}
              </span>
            ))}
          </div>
        </div>

        <div className="mb-2">
          <p className="text-xs font-medium text-gray-700 mb-1">
            Attributes to Analyze ({selectedAttributes.length}):
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedAttributes.map((attr, idx) => (
              <span
                key={`${attr.object_type}-${attr.attribute_name}-${idx}`}
                className="inline-block px-2 py-1 text-xs bg-blue-600 text-white rounded"
              >
                {attr.object_type}: {attr.attribute_name}
              </span>
            ))}
          </div>
        </div>

        {(
          (dateRange && dateRange.enabled && (dateRange.startDate || dateRange.endDate)) ||
          (edgeDurationFilters && edgeDurationFilters.length > 0) ||
          (edgeContainmentFilters && edgeContainmentFilters.enabled && edgeContainmentFilters.edges.length > 0) ||
          (activityNodeFilters && activityNodeFilters.enabled && activityNodeFilters.nodes.length > 0) ||
          (eventAttributeFilters && eventAttributeFilters.enabled && eventAttributeFilters.conditions && eventAttributeFilters.conditions.some(c => c.attribute && c.value)) ||
          (objectAttributeFilters?.enabled && objectAttributeFilters?.conditions?.some(c => c.attribute && c.value))
        ) ? (
          <div className="mb-2">
            <p className="text-xs font-medium text-gray-700 mb-1">Filters Applied:</p>
            <ul className="list-disc list-inside text-xs text-gray-600 space-y-1">
              {dateRange && dateRange.enabled && (dateRange.startDate || dateRange.endDate) && (
                <li>Date Range: {dateRange.startDate || 'N/A'} to {dateRange.endDate || 'N/A'}</li>
              )}
              {edgeDurationFilters && edgeDurationFilters.length > 0 && (
                <li>Edge Duration Filters: {edgeDurationFilters.length} active</li>
              )}
              {edgeContainmentFilters && edgeContainmentFilters.enabled && edgeContainmentFilters.edges.length > 0 && (
                <li>Edge Containment: {edgeContainmentFilters.edges.length} edges ({edgeContainmentFilters.mode})</li>
              )}
              {activityNodeFilters && activityNodeFilters.enabled && activityNodeFilters.nodes.length > 0 && (
                <li>
                  Activity Node Filter ({activityNodeFilters.mode}):
                  <ul className="list-disc list-inside ml-4 mt-0.5 space-y-0.5">
                    {activityNodeFilters.nodes.map((node, i) => (
                      <li key={i}>{node}</li>
                    ))}
                  </ul>
                </li>
              )}
              {eventAttributeFilters && eventAttributeFilters.enabled && eventAttributeFilters.conditions && eventAttributeFilters.conditions.some(c => c.attribute && c.value) && (
                <li>
                  Event Attribute Filter ({eventAttributeFilters.mode}, {eventAttributeFilters.matchType}):
                  <ul className="list-disc list-inside ml-4 mt-0.5 space-y-0.5">
                    {eventAttributeFilters.conditions.filter(c => c.attribute && c.value).map((cond, i) => (
                      <li key={i}>{cond.attribute} = {cond.value}</li>
                    ))}
                  </ul>
                </li>
              )}
              {objectAttributeFilters?.enabled && objectAttributeFilters?.conditions?.some(c => c.attribute && c.value) && (
                <li>
                  Object Attribute Filter ({objectAttributeFilters.mode}, {objectAttributeFilters.matchType}):
                  <ul className="list-disc list-inside ml-4 mt-0.5 space-y-0.5">
                    {objectAttributeFilters.conditions.filter(c => c.attribute && c.value).map((cond, i) => (
                      <li key={i}>{cond.attribute} = {cond.value}</li>
                    ))}
                  </ul>
                </li>
              )}
            </ul>
          </div>
        ) : (
          <div className="mb-2 p-2 bg-yellow-100 border border-yellow-300 rounded">
            <p className="text-xs font-medium text-yellow-800">⚠️ Warning: No filters applied</p>
            <p className="text-xs text-yellow-700">
              Go back to Step 2 and apply a filter (Date Range, Edge Duration, etc.) to define an anomalous subset for analysis.
            </p>
          </div>
        )}

        <button
          onClick={onBack}
          className="mt-3 text-sm text-blue-600 hover:underline"
        >
          ← Back to Statistics
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 font-medium">Error</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={handleAnalyze}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* Results Display */}
      {results && !loading && (
        <>
          {/* Results Summary */}
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
            <div className="text-sm text-green-800">
              <strong>Analysis Complete:</strong> Found {results.results.length} attribute values,
              {' '}{results.results.filter(r => r.p_value < significanceThreshold).length} significant (p &lt; 0.05).
              <br />
              Filtered subset: {results.total_objects_filtered} objects |
              Total dataset: {results.total_objects_unfiltered} objects
              <br />
            </div>
          </div>

          {/* Selection action bar */}
          {totalResultsCount > 0 && (
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs text-gray-600">
                {selectedKeys.size > 0
                  ? `${selectedKeys.size} row${selectedKeys.size === 1 ? '' : 's'} selected`
                  : 'Select rows to apply them as a global filter'}
              </p>
              <button
                onClick={() => setFilterModalOpen(true)}
                disabled={selectedKeys.size === 0}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Apply Selection as Filter{selectedKeys.size > 0 ? ` (${selectedKeys.size})` : ''}
              </button>
            </div>
          )}

          {/* Results Table */}
          <div className="flex-1 overflow-auto border border-gray-300 rounded-lg">
            {totalResultsCount > 0 ? (
              <table className="min-w-full border-collapse border border-gray-300">
                <thead className="bg-gray-100 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-center border border-gray-300 w-10">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        title={allSelected ? 'Deselect all' : 'Select all'}
                      />
                    </th>
                    <SortableHeader column="object_type" onSort={handleSort} activeSortBy={sortBy} sortAsc={sortAsc} onReset={handleReset}>Object Type</SortableHeader>
                    <SortableHeader column="attribute" onSort={handleSort} activeSortBy={sortBy} sortAsc={sortAsc} onReset={handleReset}>Attribute</SortableHeader>
                    <SortableHeader column="value" onSort={handleSort} activeSortBy={sortBy} sortAsc={sortAsc} onReset={handleReset}>Value</SortableHeader>
                    <SortableHeader column="direction" onSort={handleSort} activeSortBy={sortBy} sortAsc={sortAsc} onReset={handleReset}>Status</SortableHeader>
                    <SortableHeader column="lift" onSort={handleSort} activeSortBy={sortBy} sortAsc={sortAsc} onReset={handleReset}>Lift</SortableHeader>
                    <SortableHeader column="chi_squared" onSort={handleSort} activeSortBy={sortBy} sortAsc={sortAsc} onReset={handleReset}>
                      <span title="Chi-squared: Measures strength of association between attribute value and the filtered subset. Default sort.">Chi²</span>
                    </SortableHeader>
                    <SortableHeader column="percentage_filtered" onSort={handleSort} activeSortBy={sortBy} sortAsc={sortAsc} onReset={handleReset}>Filtered %</SortableHeader>
                    <SortableHeader column="percentage_unfiltered" onSort={handleSort} activeSortBy={sortBy} sortAsc={sortAsc} onReset={handleReset}>Base %</SortableHeader>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedResults).map(([objectType, attributes], objTypeIdx) =>
                    Object.entries(attributes).map(([attribute, values], attrIdx) =>
                      values.map((result, valueIdx) => {
                        // Exclude the first object type from getting thick border
                        const isFirstObjectType = objTypeIdx === 0;
                        // First row of object type gets full-width thick border (except first object type)
                        const isObjectTypeStart = attrIdx === 0 && valueIdx === 0 && !isFirstObjectType;
                        // First row of each attribute (but not first attribute) gets thick border on data columns only
                        const isAttributeStart = valueIdx === 0 && attrIdx > 0;

                        return (
                          <tr
                            key={`${objectType}-${attribute}-${result.value}`}
                            className={`${
                              result.direction === 'over_represented'
                                ? 'bg-red-50'
                                : result.direction === 'under_represented'
                                ? 'bg-blue-50'
                                : 'bg-gray-50'
                            } hover:opacity-80 transition-opacity`}
                          >
                            {/* Selection checkbox */}
                            <td className={`px-3 py-2 text-center border border-gray-300 ${isObjectTypeStart ? 'border-t-4 border-t-gray-700' : ''}`}>
                              <input
                                type="checkbox"
                                checked={selectedKeys.has(rowKey(result))}
                                onChange={() => toggleRowSelection(rowKey(result))}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                              />
                            </td>

                            {/* Object Type */}
                            <td className={`px-3 py-2 text-sm font-medium text-gray-900 border border-gray-300 ${isObjectTypeStart ? 'border-t-4 border-t-gray-700' : ''}`}>
                              {result.object_type}
                            </td>

                            {/* Attribute */}
                            <td className={`px-3 py-2 text-sm text-gray-700 border border-gray-300 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}>
                              {attribute}
                            </td>

                            {/* Value (truncated with title) */}
                            <td
                              className={`px-3 py-2 text-sm text-gray-800 border border-gray-300 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}
                              title={result.value}
                            >
                              {result.value.length > 50 ? `${result.value.substring(0, 50)}...` : result.value}
                            </td>

                            {/* Status Badge */}
                            <td className={`px-3 py-2 text-center border border-gray-300 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}>
                              <span
                                className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                                  result.direction === 'over_represented'
                                    ? 'bg-red-200 text-red-800'
                                    : 'bg-blue-200 text-blue-800'
                                }`}
                              >
                                {result.direction === 'over_represented' ? 'Over-represented' : 'Under-represented'}
                              </span>
                            </td>

                            {/* Lift */}
                            <td className={`px-3 py-2 text-right text-sm font-medium text-gray-900 border border-gray-300 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}>
                              {result.lift.toFixed(2)}×
                            </td>

                            {/* Chi-squared */}
                            <td className={`px-3 py-2 text-right text-sm text-gray-800 border border-gray-300 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}>
                              {result.chi_squared != null ? result.chi_squared.toFixed(2) : '—'}
                            </td>

                            {/* Frequency in Selection */}
                            <td className={`px-3 py-2 text-right text-sm text-gray-800 border border-gray-300 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}>
                              {result.percentage_filtered.toFixed(1)}% ({result.count_filtered})
                            </td>

                            {/* Frequency Total */}
                            <td className={`px-3 py-2 text-right text-sm text-gray-800 border border-gray-300 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}>
                              {result.percentage_unfiltered.toFixed(1)}% ({result.count_unfiltered})
                            </td>
                          </tr>
                        );
                      })
                    )
                  )}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center">
                <p className="text-gray-600 font-medium">No Significant Results Found</p>
                <p className="text-sm text-gray-500 mt-2">
                  No statistically significant results (p &lt; 0.05) found with current filters
                </p>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="mt-4 p-3 bg-gray-50 border border-gray-300 rounded text-xs text-gray-700">
            <strong>Legend:</strong>
            <ul className="ml-4 mt-1 list-disc">
              <li><strong>Status:</strong> Classification based on Lift value (Over-represented: Lift &gt; 1.0, Under-represented: Lift &lt; 1.0)</li>
              <li><strong>Lift:</strong> Ratio of relative frequency in selection vs. total (2.5× = value is 2.5 times more frequent in selection)</li>
              <li><strong>Frequency in Selection:</strong> Percentage and count of this value in the filtered subset</li>
              <li><strong>Frequency Total:</strong> Percentage and count of this value in the entire dataset</li>
              <li><strong>Quality Gate:</strong> Only statistically significant results shown (p &lt; 0.05)</li>
            </ul>
          </div>
        </>
      )}

      {/* Initial State */}
      {!hasAnalyzed && !loading && (
        <div className="flex-1 flex items-center justify-center bg-gray-50 border border-gray-300 rounded-lg">
          <div className="text-center">
            <p className="text-gray-600 font-medium">Ready to Perform Root Cause Analysis</p>
            <p className="text-sm text-gray-500 mt-2">
              Click "Perform Root Cause Analysis" to identify statistically significant patterns
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Analysis will compare filtered subset vs. base dataset using Chi-squared test
            </p>
          </div>
        </div>
      )}
      </div>
      {/* End of scrollable container */}

      {/* Apply-selection-as-global-filter modal */}
      <ApplyFilterModal
        open={filterModalOpen}
        onOpenChange={setFilterModalOpen}
        selectedConditions={selectedConditions}
      />
    </div>
  );
};

export default Step4_RootCauseResults;
